import React, { FC, useMemo } from "react";
import { User } from "../../models/users/userInterfaces";
import {
  ActivityItem,
  NotificationType,
  DecisionStatusLog,
  IdvRecord,
  OnboardingApplicationUpdateLog,
} from "../../models/applications/applicationInterfaces";
import { orderBy } from "lodash";
import { ActivityFeedItem } from "./activityItem";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectApplicationDetail } from "../../models/applications/applicationDetailSlice";
import { selectMyUser } from "../../models/users/myUserSlice";

interface Props {
  activity: ActivityItem[];
  user?: User;
  decisionStatusLogs: DecisionStatusLog[];
  onboardingApplicationUpdateLogs: OnboardingApplicationUpdateLog[];
}
export const Activity: FC<Props> = ({
  activity,
  user,
  decisionStatusLogs,
  onboardingApplicationUpdateLogs,
}) => {
  const application = useSelector(selectApplicationDetail);
  // The admin flag lives on the signed-in user, not on the application's applicant —
  // same source ActivityFeedItem reads for its admin-only subtitles.
  const myUser = useSelector(selectMyUser);
  const appDetails = useMemo(() => application.application, [application]);
  let isMyApplication: boolean | undefined = undefined;
  if (user && appDetails) {
    isMyApplication = user?.userId === appDetails?.user?.userId;
  }

  if (!appDetails?.created_at) return null;

  // Permissions Note: Any user with any permissions can view the activity feed.

  return (
    <div id="activity-container">
      <div className="activity-feed">
        {orderBy(
          [
            ...activity,
            ...mapDecisionStatusLogsToActivityItems(decisionStatusLogs),
            ...mapOnboardingApplicationUpdateLogsToActivityItems(
              onboardingApplicationUpdateLogs
            ),
            ...mapIdvRequestsToActivityItems(
              application.idvData?.records,
              myUser?.isAdminPortalUser
            ),
          ],
          (activity) => moment.utc(activity.timestamp).unix(),
          "desc"
        ).map((item) => (
          <ActivityFeedItem activity={item} key={item.id} />
        ))}
        {/* The first item (bottom of the list) is always "application received" */}
        <ActivityFeedItem
          activity={{
            id: 0,
            timestamp: appDetails.created_at,
            payload: {
              icon: "document-text-outline",
              notificationType: NotificationType.application_received,
            },
          }}
        />
      </div>
    </div>
  );
};

/**
 * Admin-requested document verifications, as timeline entries.
 *
 * Derived from the attempts the page already loads rather than from a `pulses` row:
 * the attempt records who asked and when, so deriving keeps one source of truth and
 * needs no Rails worker. The trade-off is that this entry lives only in the client —
 * an export or an API consumer of /feed will not see it.
 *
 * ADMIN ONLY. The /feed endpoint filters admin-only notification types server-side
 * for applicants; a client-side source has to do its own filtering, and who asked for
 * a verification is internal.
 */
const mapIdvRequestsToActivityItems = (
  records?: IdvRecord[],
  isAdminPortalUser?: boolean
): ActivityItem[] => {
  if (!records || !isAdminPortalUser) {
    return [];
  }

  return records.flatMap((record) =>
    (record.sessions ?? [])
      // Only a reviewer's request is an admin action. In-flow and at-submission
      // attempts happen without anyone asking, and the applicant's own progress is
      // already the subject of other entries.
      .filter((session) => session.origin === "admin")
      .map((session) => ({
        id: `idv-request-${session.id}`,
        timestamp: session.createdAt,
        fromUser: session.requestedBy
          ? {
              id: session.requestedBy.id,
              firstName: session.requestedBy.firstName ?? "",
              lastName: session.requestedBy.lastName ?? "",
            }
          : undefined,
        payload: {
          notificationType:
            NotificationType.submission_document_verification_requested,
          // "document-text-outline" is already used by the feed's own
          // application-received item, so it is known to resolve. ionicons fetches
          // each icon lazily from a CDN, so an invented name fails at runtime rather
          // than at build — "id-card-outline" threw "Failed to fetch" per entry.
          icon: "document-text-outline",
          personName: `${record.person.firstName ?? ""} ${
            record.person.lastName ?? ""
          }`.trim(),
        },
      }))
  );
};

const mapDecisionStatusLogsToActivityItems = (
  decisionStatusLogs?: DecisionStatusLog[]
): ActivityItem[] => {
  if (!decisionStatusLogs) {
    return [];
  }

  // Sort the decision status logs by createdAt in descending order, filtering out any done in test mode.
  return decisionStatusLogs
    .filter(
      (decisionStatusLog) =>
        decisionStatusLog.newDecisionStatus?.uuid &&
        decisionStatusLog.newDecisionStatus.uuid !==
          decisionStatusLog.oldDecisionStatus?.uuid
    )
    .map((decisionStatusLog, index) => ({
      id: `decision-${decisionStatusLog.createdAt}-${index}`,
      timestamp: decisionStatusLog.createdAt,
      fromUser: decisionStatusLog.user,
      payload: {
        notificationType:
          decisionStatusLog.decisionRule === undefined
            ? NotificationType.submission_decision_status_updated
            : NotificationType.submission_decision_status_auto_updated,
        icon: "flag-outline",
        oldDecisionStatus: decisionStatusLog.oldDecisionStatus?.title,
        newDecisionStatus: decisionStatusLog.newDecisionStatus?.title,
        decisionRuleTitle: decisionStatusLog.decisionRule?.title,
        message: decisionStatusLog.comment,
      },
    }));
};

const formatChangedFields = (
  original: any,
  updated: any
): Array<{ description: string; value: string }> => {
  const changes: Array<{ description: string; value: string }> = [];

  const allKeys = new Set([
    ...Object.keys(original || {}),
    ...Object.keys(updated || {}),
  ]);

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return "Not set";
    if (typeof value === "string") return value;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  for (const key of Array.from(allKeys)) {
    const originalValue = original?.[key];
    const updatedValue = updated?.[key];

    if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
      const formattedKey = key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
      const formattedOriginal = formatValue(originalValue);
      const formattedUpdated = formatValue(updatedValue);

      changes.push({
        description: formattedKey,
        value: `${formattedOriginal} → ${formattedUpdated}`,
      });
    }
  }

  return changes;
};

const mapOnboardingApplicationUpdateLogsToActivityItems = (
  onboardingApplicationUpdateLogs?: OnboardingApplicationUpdateLog[]
): ActivityItem[] => {
  if (!onboardingApplicationUpdateLogs) {
    return [];
  }

  // Sort the application update logs by createdAt in descending order, filtering out any done in test mode.
  return onboardingApplicationUpdateLogs.map(
    (onboardingApplicationUpdateLog, index) => {
      let original: any;
      let updated: any;

      try {
        original = JSON.parse(onboardingApplicationUpdateLog.original);
        updated = JSON.parse(onboardingApplicationUpdateLog.updated);
      } catch (error) {
        // If parsing fails, use the raw strings
        original = onboardingApplicationUpdateLog.original;
        updated = onboardingApplicationUpdateLog.updated;
      }

      const changes = formatChangedFields(original, updated);

      return {
        id: `app-update-${onboardingApplicationUpdateLog.createdAt}-${index}`,
        timestamp: onboardingApplicationUpdateLog.createdAt,
        fromUser: onboardingApplicationUpdateLog.user,
        payload: {
          notificationType: NotificationType.application_updated,
          icon: "create-outline",
          slug: onboardingApplicationUpdateLog.slug,
          applicationChanges: changes,
        },
      };
    }
  );
};
